import type { Crm } from '../crm';

import { LOW_CREDITS_THRESHOLD } from './constants';
import type { CompanyQuota, DataPointCost, Quota, QuotaStatus } from './types';

const DEFAULT_LEGACY_PRICE = 1;

export const decreaseQuota = (quota: Quota, usedCredits = 1): Quota => ({
  ...quota,
  used: quota.used + usedCredits,
  remaining: quota.remaining - usedCredits,
});

export const getQuotaStatus = (remaining: number, total: number): QuotaStatus => {
  if (total <= 0 || remaining <= 0) {
    return 'depleted';
  }

  if (remaining / total <= LOW_CREDITS_THRESHOLD) {
    return 'low';
  }

  return 'normal';
};

export const isLowCreditsStatus = (status: QuotaStatus): boolean => status === 'low';
export const isDepletedCreditsStatus = (status: QuotaStatus): boolean => status === 'depleted';

/**
 * Supports legacy approach to quota management: `QuotaStatus` should be used instead to determine the state
 */
export const isOutOfQuota = (quota: Quota | CompanyQuota | undefined): boolean => !!quota && quota.remaining <= 0;

export const calculateRevealCost = (contactCount: number, pricePerContact: number): number =>
  contactCount * pricePerContact;

export const canAffordReveal = (remaining: number, contactCount: number, pricePerContact: number): boolean =>
  remaining >= calculateRevealCost(contactCount, pricePerContact);

/**
 * Legacy mode: check if reveal is affordable (price is always 1)
 */
export const canAffordRevealLegacy = (remaining: number, count: number): boolean =>
  remaining >= count * DEFAULT_LEGACY_PRICE;

/**
 * Legacy mode: get reveal price (always 1)
 */
export const getRevealPriceLegacy = (count: number): number => count * DEFAULT_LEGACY_PRICE;

/**
 * Get CRM-specific export price from data points
 */
export const getCrmExportPrice = (dataPoints: DataPointCost, crm: Crm): number => dataPoints.company[crm];

/**
 * Check if export is affordable for given CRM
 */
export const canAffordCrmExport = (remaining: number, dataPoints: DataPointCost, crm: Crm): boolean =>
  remaining >= getCrmExportPrice(dataPoints, crm);

/**
 * Legacy mode: check if export is affordable (price is always 1)
 */
export const canAffordExportLegacy = (remaining: number): boolean => remaining > 0;

/**
 * Legacy mode: get export price (always 1)
 */
export const getExportPriceLegacy = (): number => DEFAULT_LEGACY_PRICE;
